import type { AffinityPartner } from 'src/components/partner-codes/partner-codes.types';
import { ValidationOutcomes, ValidationResult } from '.';

const toBoolean = (value: unknown, fallback: boolean = false): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }
  return fallback;
};

const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isNaN(value) ? null : value;
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

const sanitisePartner = (
  partner: unknown,
  index: number,
  messages: string[],
): AffinityPartner | null => {
  if (!partner || typeof partner !== 'object') {
    messages.push(`Partner entry at index ${index} is not an object.`);
    return null;
  }
  const partnerData = partner as Record<string, unknown>;
  const id = toNumber(partnerData.id);
  const name =
    typeof partnerData.name === 'string' ? partnerData.name.trim() : '';

  if (id === null) {
    messages.push(
      `Partner entry at index ${index} is missing a valid affinityPartnerId.`,
    );
    return null;
  }

  if (!name) {
    messages.push(`Partner entry at index ${index} is missing a valid name.`);
    return null;
  }

  const description =
    typeof partnerData.description === 'string' ? partnerData.description : '';

  const logo =
    typeof partnerData.logo === 'string' && partnerData.logo.trim().length > 0
      ? partnerData.logo.trim().replaceAll(' ', '%20')
      : '';

  return {
    id,
    name,
    description,
    logo,
  };
};

const ValidatePartnerCodesComponentProps = (props: any): ValidationResult => {
  const vr: ValidationResult = {
    result: ValidationOutcomes.SUCCESS,
    messages: [],
    componentProps: props,
  };

  const sanitisedPartners: AffinityPartner[] = [];
  let disableStepOne = false;

  if (!props || typeof props !== 'object') {
    vr.result = ValidationOutcomes.FAILURE;
    vr.messages.push('Partner Codes component props are missing.');
    return vr;
  }

  disableStepOne = toBoolean(props.disableStepOne, false);

  if (
    props.hasOwnProperty('confirmationStepImage') &&
    typeof props.confirmationStepImage === 'string'
  ) {
    props.confirmationStepImage = props.confirmationStepImage
      .trim()
      .replaceAll(' ', '%20');
  }

  if (
    props.hasOwnProperty('confirmationStepImageAlt') &&
    typeof props.confirmationStepImageAlt === 'string'
  ) {
    props.confirmationStepImageAlt = props.confirmationStepImageAlt.trim();
  }

  if (Array.isArray(props.partners)) {
    props.partners.forEach((partner: unknown, index: number) => {
      const sanitised = sanitisePartner(partner, index, vr.messages);
      if (sanitised) {
        sanitisedPartners.push(sanitised);
      }
    });

    if (sanitisedPartners.length !== props.partners.length) {
      vr.result = ValidationOutcomes.WARNING;
    }
  } else {
    vr.result = ValidationOutcomes.WARNING;
    vr.messages.push(
      'No partner data supplied by AEM; component will rely on runtime data.',
    );
  }

  vr.componentProps = {
    ...props,
    partners: sanitisedPartners,
    disableStepOne,
  };

  return vr;
};

export default ValidatePartnerCodesComponentProps;
